import { useCallback } from 'react';
import { ReferenceDataValue } from 'entities/document';
import { useBatchCreateTags, useContinueTag, TagType } from 'entities/tag';
import { useAddTagsToCells } from 'entities/reference-data';
import { useTagSelection } from 'app/state/contexts/TagContext';
import { useEditorReferences, useSetEditorReferences } from 'app/api/documents/useEditorData';

interface Cell {
  row: number;
  col: number;
  value?: string | number | null;
  conceptId?: string;
  linkroleUri?: string | null;
}

interface SectionContext {
  documentSuperId: number;
  sectionSuperId: string;
  pageId: string;
}

interface BaseInput {
  cells: Cell[];
  referenceDataSuperId: string;
  conceptId: string;
  type: TagType;
  taxonomyId: string;
  sectionContext: SectionContext;
  locale: string;
  onTagsCreated?: () => void;
  keepSelection?: boolean;
  blockedTagIds?: Set<string>;
  origin?: 'human' | 'ai';
  linkroleUri?: string | null;
}

export type ApplyCellTagsInput = (BaseInput & { mode: 'create' }) | (BaseInput & { mode: 'continue'; startPredecessorId: string });

export interface ApplyCellTagsResult {
  createdCount: number;
  appliedTagIds: string[];
}

export const useApplyCellTags = () => {
  const { mutateAsync: batchCreateTags } = useBatchCreateTags();
  const { mutate: continueTag } = useContinueTag();
  const { mutateAsync: addTagsToCells } = useAddTagsToCells();
  const references = useEditorReferences();
  const setEditorReferences = useSetEditorReferences();
  const { clearTableTagSelection } = useTagSelection();

  return useCallback(
    async (input: ApplyCellTagsInput): Promise<ApplyCellTagsResult> => {
      const { cells, referenceDataSuperId, conceptId, type, taxonomyId, sectionContext, locale, onTagsCreated, blockedTagIds, origin, linkroleUri } = input;
      const referenceData = references?.find((r) => r.superId === referenceDataSuperId);
      if (!referenceData) return { createdCount: 0, appliedTagIds: [] };

      const cellsToTag = cells.filter((cell) => {
        if (!blockedTagIds || blockedTagIds.size === 0) return true;
        const existingTagIds = referenceData.selectionContent?.[cell.row]?.[cell.col]?.tags ?? [];
        return !existingTagIds.some((id: string) => blockedTagIds.has(id));
      });
      if (cellsToTag.length === 0) return { createdCount: 0, appliedTagIds: [] };

      const createdTags: { tagId: string; row: number; col: number }[] = [];

      if (input.mode === 'create') {
        try {
          const bodies = cellsToTag.map((cell) => ({
            taxonomyId,
            documentSuperId: sectionContext.documentSuperId,
            sectionSuperId: sectionContext.sectionSuperId,
            pageId: sectionContext.pageId,
            onId: referenceDataSuperId,
            conceptId: cell.conceptId ?? conceptId,
            lang: locale,
            type,
            value: cell.value != null ? String(cell.value) : undefined,
            ...(origin ? { origin } : {}),
            ...((cell.linkroleUri ?? linkroleUri) != null ? { linkroleUri: cell.linkroleUri ?? linkroleUri } : {}),
          }));
          const created = await batchCreateTags(bodies);
          created.forEach((tag, i) => createdTags.push({ tagId: tag.id, row: cellsToTag[i].row, col: cellsToTag[i].col }));
        } catch (error) {
          // Partial success is reconciled via createdTags below; surface the failure (user-known ids only).
          const status = (error as { response?: { status?: number } })?.response?.status;
          const reason = error instanceof Error ? error.message : String(error);
          const cells = cellsToTag.map((c) => `r${c.row}c${c.col}`).join(', ');
          console.warn(`Failed to apply tags in document ${sectionContext.documentSuperId} (cells ${cells})${status ? ` — HTTP ${status}` : ''}: ${reason}`);
        }
      } else {
        let previousTagId = input.startPredecessorId;
        for (const cell of cellsToTag) {
          const predecessorForCell = previousTagId;
          try {
            const newTag = await new Promise<{ id: string }>((resolve, reject) => {
              continueTag(
                {
                  taxonomyId,
                  documentSuperId: sectionContext.documentSuperId,
                  sectionSuperId: sectionContext.sectionSuperId,
                  pageId: sectionContext.pageId,
                  onId: referenceDataSuperId,
                  conceptId,
                  lang: locale,
                  type,
                  value: cell.value != null ? String(cell.value) : undefined,
                  ...(linkroleUri != null ? { linkroleUri } : {}),
                  predecessorId: predecessorForCell,
                },
                {
                  onSuccess: (data: { id: string }) => resolve(data),
                  onError: (error: unknown) => reject(error),
                }
              );
            });
            createdTags.push({ tagId: newTag.id, row: cell.row, col: cell.col });
            previousTagId = newTag.id;
          } catch (error) {
            // Partial success is reconciled via createdTags below; surface the failure (user-known ids only).
            const status = (error as { response?: { status?: number } })?.response?.status;
            const reason = error instanceof Error ? error.message : String(error);
            console.warn(
              `Failed to apply tag to cell r${cell.row}c${cell.col} in document ${sectionContext.documentSuperId}${
                status ? ` — HTTP ${status}` : ''
              }: ${reason}`
            );
          }
        }
      }

      if (createdTags.length === 0) return { createdCount: 0, appliedTagIds: [] };

      let writtenBack = false;
      try {
        const result = await addTagsToCells({ referenceData, tagsToAdd: createdTags });
        const updatedReferenceData = result?.data as ReferenceDataValue | undefined;
        if (updatedReferenceData) {
          const updatedReferences = references.map((ref) => (ref.superId === updatedReferenceData.superId ? updatedReferenceData : ref));
          setEditorReferences(() => updatedReferences);
        }
        if (!input.keepSelection) {
          onTagsCreated?.();
          clearTableTagSelection?.();
        }
        writtenBack = true;
      } catch (error) {
        console.error('Error writing tag ids back to reference data:', error);
      }

      return { createdCount: createdTags.length, appliedTagIds: writtenBack ? createdTags.map((t) => t.tagId) : [] };
    },
    [batchCreateTags, continueTag, addTagsToCells, references, clearTableTagSelection, setEditorReferences]
  );
};
